import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Load image and convert to base64
 */
const loadImageAsBase64 = (imagePath) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = (error) => {
      console.error('Failed to load image:', imagePath, error);
      reject(error);
    };
    img.src = `${process.env.PUBLIC_URL}${imagePath}`;
  });
};

/**
 * Generate Scope of Accreditation PDF (matching PHP design)
 */
export const generateScopeOfAccreditation = async (data = {}) => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = 210;
  const pageHeight = 297;

  console.log(data.records, 'Scope data for PDF')

  // Dummy data matching PHP structure
  const scopeData = {
    labName: data.labName || '',
    location: data.location || '',
    address: data.address || '',
    standard: data.standard || '',
    certificateNumber: data.certificateNumber || '',
    issueDate: data.issueDate || '',
    validUntil: data.validUntil || '',
    amendmentDate: data.amendmentDate || '',
    applicationNo: data.applicationNo || '',
    testPerformedAt: data.testPerformedAt || '',
    headerChange: data.headerChange || false,
    labTypeId: data.labTypeId || false,
    records: data.records || []
  };



  try {
    // Load images (matching CertificateGenerator.js)
    let headerImage, logoImage;

    // Load header image (try multiple filenames)
    try {
      headerImage = await loadImageAsBase64('/certificate_images/scopeCrystalnew1.png');
      console.log('✓ Header image loaded (scopeCrystalnew1.png)');
    } catch (err) {
      console.warn('✗ Header image not found (tried scopeCrystalnew1.png)');
    }

    // Load logo image
    try {
      logoImage = await loadImageAsBase64('/certificate_images/certificate_logo.png');
      console.log('✓ Logo image loaded');
    } catch (err) {
      console.warn('✗ Logo image not found');
    }

    console.log('Logo image status:', logoImage ? 'Loaded ✓' : 'Not loaded ✗');

    let startY = 5;

    // Set startY for main table (leave space for header that will be drawn in didDrawPage)
    const leftMargin = 5;
    const rightMargin = 5;

    const labText = `${scopeData.labName}${scopeData.address ? ', ' + scopeData.address : ''}`;
    const maxWidth = pageWidth - (leftMargin + 55) - rightMargin;

    doc.setFontSize(10.5);
    const wrappedText = doc.splitTextToSize(labText, maxWidth);

    const dynamicHeaderHeight =
      (headerImage ? 43 : 10) +       // header start
      (wrappedText.length * 6) +      // lab name lines
      7 +                             // accreditation row
      7 +                             // certificate row
      7 +                             // validity row
      10;                             // spacing

    startY = dynamicHeaderHeight + 10;
    const sanitizeText = (text) => {
      if (!text) return text;
      return text
        .replace(/α/g, "alpha")
        .replace(/β/g, "beta")
        .replace(/±/g, "+/-");
    };

    // Main scope table (matching PHP columns: 6%, 17%, 37%, 20%, 20%)
const tableColumn = scopeData.labTypeId === 3
  ? [
      'S.No',
      'Discipline / Group',
      'Test Code',
      'Materials or Products tested',
      'Component, parameter or characteristic tested / Specific Test Performed / Tests or type of tests performed',
      'Test Method Specification against which tests are performed and / or the techniques / equipment used'
    ]
  : [
      'S.No',
      'Discipline / Group',
      'Measurand or Reference Material/Type of instrument or material to be calibrated or measured / Quantity Measured /Instrument',
      'Calibration or Measurement Method or procedure',
      'Measurement range and additional parameters where applicable(Range and Frequency)',
      '* Calibration and Measurement Capability(CMC)(±)'
    ];


    // Group records by testPerformedAt
    // const groupedData = scopeData.records.reduce((acc, item) => {
    //   const facility = item.testPerformedAt || "OtherFacility";

    //   if (!acc[facility]) {
    //     acc[facility] = [];
    //   }

    //   acc[facility].push(item);
    //   return acc;
    // }, {});

    // Prepare rows with heading row first, then data rows
    const tableRows = [];
    let srNo = 1;

    // Group records by testPerformedAt
    const groupedData = scopeData.records.reduce((acc, item) => {
      const facility = item.testPerformedAt || "";

      if (!acc[facility]) {
        acc[facility] = [];
      }

      acc[facility].push(item);
      return acc;
    }, {});

    // Facility Name Mapping
    const facilityMap = {
      MobileFacility: "POCT (Point of Care Testing)",
      TemporaryFacility: "Temporary Facility",
      PermanentFacility: "Permanent Facility",
      laboratorySiteTesting: "Site Facility",
      poctTesting: "POCT (Point of Care Testing)",
      permanentSiteFacility: "Permanent Site Facility"
    };

    Object.keys(groupedData).forEach((facility, groupIndex) => {
      // Handle comma separated + line break separated values
      const facilities = facility
        ? facility
          .split(/,|\n|\r\n/)
          .map((f) => f.trim())
          .filter((f) => f !== "")
        : [];

      // Facility Mapping
      const facilityMap = {
        PermanentFacility: "Permanent Facility",
        PermanentFacilityAddress: "Permanent Facility",

        SiteFacility: "Site Facility",
        laboratorySiteTesting: "Site Facility",

        MobileFacility: "POCT (Point of Care Testing)",
        poctTesting: "POCT (Point of Care Testing)",
        POCT: "POCT (Point of Care Testing)",
        "POCT (Point of Care Testing)": "POCT (Point of Care Testing)",

        TemporaryFacility: "Temporary Facility",
        permanentSiteFacility: "Permanent Site Facility"
      };

      // Convert + remove duplicate
      const formattedFacilities = [
        ...new Set(
          facilities.map((f) => facilityMap[f] || f)
        )
      ];

      const facilityHeading =
        formattedFacilities.length > 0
          ? formattedFacilities.join(", ")
          : "N/A";

      tableRows.push([
        {
          content: facilityHeading,
          colSpan: 6,
          styles: {
            halign: "center",
            fontStyle: "bold",
            fillColor: [220, 220, 220],
            textColor: [0, 0, 0],
            fontSize: 10
          },
          pageBreak: groupIndex !== 0 ? "before" : undefined
        }
      ]);

      groupedData[facility].forEach((record, index) => {
        if (scopeData.labTypeId === 3) {
          tableRows.push([
            (index + 1).toString(),
            record.discipline,
            record.testcode,
            record.productMaterial,
            record.specificTest,
            sanitizeText(record.scopeYear)
          ]);
        } else {
          tableRows.push([
            (index + 1).toString(),
            record.discipline,
            record.productMaterial,
            record.specificTest,
            record.range,
            sanitizeText(record.scopeYear),
          ]);
        }
      });
    });


    const headerHeight = headerImage ? 110 : 80;
    const totalPagesExp = "{total_pages_count_string}";
    autoTable(doc, {
      startY: startY,
      margin: { left: 3, right: 3, bottom: 30, top: headerHeight },
      head: [tableColumn],
      body: tableRows,
      theme: 'grid',
      rowPageBreak: 'avoid',

      styles: {
        fontSize: 9,
        cellPadding: 1,
        lineColor: [0, 0, 0],
        lineWidth: 0.3,
        overflow: 'linebreak',
        cellWidth: 'wrap',
        fillColor: false,
        textColor: [0, 0, 0],
        halign: 'left',
        valign: 'middle'
      },

      didParseCell: function (data) {
        if (data.column.index === 5) {
          let text = data.cell.raw || "";
          text = text.replace(/-/g, "- ");
          data.cell.text = text;
        }
      },
      headStyles: {
        fillColor: false,
        textColor: [0, 0, 0],
        fontSize: 8,
        fontStyle: 'bold',
        halign: 'center',
        valign: 'middle'
      },
      bodyStyles: {
        textColor: [0, 0, 0],
        fontSize: 9,
        fontStyle: 'normal'
      },
      columnStyles: scopeData.labTypeId === 3
        ? {
            0: { cellWidth: 10, halign: 'center' },   // S.No
            1: { cellWidth: 30 },                     // Discipline
            2: { cellWidth: 20 },                     // Test Code
            3: { cellWidth: 45 },                     // Materials
            4: { cellWidth: 45 },                     // Component
            5: { cellWidth: 50 }                      // Test Method
          }
        : {
            0: { cellWidth: 10, halign: 'center' },   // S.No
            1: { cellWidth: 30 },                     // Discipline
            2: { cellWidth: 45 },                     // Calibration Method
            3: { cellWidth: 50 },                     // Measurement Range
            4: { cellWidth: 35 },                     // Range
            5: { cellWidth: 35 }                      // CMC
          },
      margin: { left: 3, right: 3, bottom: 30, top: 105 },
      didDrawPage: function (data) {
        const currentPage = data.pageNumber;
        const totalPages = doc.internal.getNumberOfPages();

        // Background watermark logo (NABL logo in center - on every page)
        if (logoImage) {
          try {
            doc.saveGraphicsState();
            doc.setGState(new doc.GState({ opacity: 0.5 }));
            const watermarkSize = 150;
            const watermarkX = (pageWidth - watermarkSize) / 2;
            const watermarkY = (pageHeight - watermarkSize) / 2;
            doc.addImage(logoImage, 'PNG', watermarkX, watermarkY, watermarkSize, watermarkSize);
            doc.restoreGraphicsState();
          } catch (err) {
            console.error('Failed to add watermark on page', currentPage, ':', err);
          }
        }

        // Header Image at top (repeats on every page)
        if (headerImage) {
          const headerWidth = doc.internal.pageSize.getWidth() - 20;
          const headerHeight = 38;
          const headerX = (pageWidth - headerWidth) / 2;
          doc.addImage(headerImage, 'PNG', headerX, 5, headerWidth, headerHeight);
        }

        const headerStartY = headerImage ? 43 : 10;

        // Header info table (repeats on every page, no border - matching PHP border="0")
        const leftMargin = 5;
        const rightMargin = 5;
        const rowHeight = 7;
        let yPos = headerStartY;

        doc.setFontSize(10.5);

        // Row 1: Laboratory Name
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Laboratory Name :', leftMargin + 2, yPos + 5);

        doc.setFont('helvetica', 'normal');

        
        const removeDuplicateAddressParts = (address) => {
          if (!address) return '';

          const parts = address
            .split(',')
            .map(part => part.trim());

          const cleaned = [];

          parts.forEach(part => {
            const upperPart = part.toUpperCase();

            if (
              cleaned.length === 0 ||
              cleaned[cleaned.length - 1].toUpperCase() !== upperPart
            ) {
              cleaned.push(part);
            }
          });

          return cleaned.join(', ');
        };

        const cleanedAddress = removeDuplicateAddressParts(scopeData.address);

        const labText = `${scopeData.labName}${cleanedAddress ? ', ' + cleanedAddress : ''}`;

        // Max width for wrapping
        const maxWidth = pageWidth - (leftMargin + 55) - rightMargin;

        // Split text
        const wrappedText = doc.splitTextToSize(labText, maxWidth);

        // Print wrapped text
        doc.text(wrappedText, leftMargin + 55, yPos + 5);

        // Adjust Y position based on number of lines
        yPos += wrappedText.length * 6;

        // Row 2: Accreditation Standard
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Accreditation Standard :', leftMargin + 2, yPos + 5);
        doc.setFont('helvetica', 'normal');
        doc.text(scopeData.standard || '', leftMargin + 55, yPos + 5);
        yPos += rowHeight;

        // Row 3: Certificate Number & Page No
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Certificate Number :', leftMargin + 2, yPos + 5);
        doc.setFont('helvetica', 'normal');
        doc.text(scopeData.certificateNumber || '', leftMargin + 55, yPos + 5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Page No :', leftMargin + 122, yPos + 5);
        doc.setFont('helvetica', 'normal');
        doc.text(`${currentPage} of ${totalPagesExp}`, leftMargin + 142, yPos + 5);
        yPos += rowHeight;

        // Row 4: Validity & Last Amended on
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Validity :', leftMargin + 2, yPos + 5);
        doc.setFont('helvetica', 'normal');
        doc.text(`${scopeData.issueDate && scopeData.validUntil ? `${scopeData.issueDate} to ${scopeData.validUntil}` : ""}`, leftMargin + 55, yPos + 5);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Last Amended on :', leftMargin + 122, yPos + 5);
        doc.setFont('helvetica', 'normal');
        doc.text(scopeData.amendmentDate || '', leftMargin + 162, yPos + 5);

        // Footer on every page (matching PHP)
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        const footerText = "This is annexure to 'Certificate of Accreditation' and does not require any signature.";
        doc.text(footerText, pageWidth / 2, pageHeight - 10, { align: 'center' });
      }
    });

    // Save PDF
    if (typeof doc.putTotalPages === 'function') {
      doc.putTotalPages(totalPagesExp);
    }
    const now = new Date();

    const timestamp =
      now.getFullYear() +
      String(now.getMonth() + 1).padStart(2, "0") +
      String(now.getDate()).padStart(2, "0") +
      "_" +
      String(now.getHours()).padStart(2, "0") +
      String(now.getMinutes()).padStart(2, "0") +
      String(now.getSeconds()).padStart(2, "0");

    const fileName = `Scope_${scopeData.certificateNumber || scopeData.applicationNo}-${timestamp}.pdf`;

    // Return blob for upload and filename
    const pdfBlob = doc.output('blob');
    console.log('✓ Scope PDF generated successfully');

    return { pdfBlob, fileName, doc };

  } catch (error) {
    console.error('Error generating scope:', error);
    throw new Error(`Failed to generate scope: ${error.message}`);
  }
};

export default {
  generateScopeOfAccreditation
};
